/**
 * Recognises the failure that occurs when an unverified account tries to write
 * something `firestore.rules` gates behind `isVerified()`.
 *
 * The rules require a verified email for every company write, and only
 * password-provider accounts can be unverified — OAuth sign-ins always arrive
 * verified. Before this was surfaced, the denial reached the user as a raw
 * "Missing or insufficient permissions", which says nothing about what to do
 * and left people with an account and no company (#512).
 */
export function isVerificationRequiredError(
  error: unknown,
  unverifiedPasswordAccount: boolean,
): boolean {
  // `permission-denied` is not specific to verification: the same company rule
  // refuses an ownerId that is not the caller, and anything isValidNewCompany()
  // rejects. Sending a verified user to /verify-email for one of those would
  // hide the real failure behind advice they cannot act on, so the caller's
  // account state decides and the error shape only confirms it.
  if (!unverifiedPasswordAccount || !error) return false;
  const code = (error as { code?: unknown }).code;
  // Separators vary by surface: the SDK code is `permission-denied`, the gRPC
  // status is `PERMISSION_DENIED`, and the human message is "Missing or
  // insufficient permissions".
  const DENIED = /permission[-_ ]denied|insufficient permissions/i;
  if (typeof code === 'string' && DENIED.test(code)) {
    return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  return DENIED.test(message);
}

/**
 * The message shown in place of the raw permissions error. Names the address so
 * a typo in the signup form is visible, and says where the link went.
 */
export function verificationRequiredMessage(email?: string): string {
  const target = email ? ` We sent a link to ${email}.` : '';
  return (
    'Please verify your email address before creating your company.' +
    target +
    ' Open the link, then continue — the page will pick up where you left off.'
  );
}

/** Router state key carrying the refusal text to the verification page. */
export const VERIFICATION_MESSAGE_STATE_KEY = 'verificationMessage';
